import { useEffect, useState } from "react"
import type { KeyboardEvent } from "react"
import type { UseFormGetValues, UseFormSetValue } from "react-hook-form"
import type { CalendarEventFormDataV2 } from "@/types/calendar"

type UseEventFormLogicArgs = {
  open: boolean
  mode: "create" | "edit"
  selectedDate?: Date
  startTime?: string
  endTime?: string
  candidateEmail?: string
  shouldAutoInjectCandidateEmail: boolean
  shouldAutoAdjustEndTime: boolean
  inviteeEmails: string[]
  getValues: UseFormGetValues<CalendarEventFormDataV2>
  setValue: UseFormSetValue<CalendarEventFormDataV2>
}

/** The last end the clocks can express: they offer the quarters, not 23:59. */
const LAST_SLOT = "23:45"

/**
 * The end time that follows a start, without wrapping past midnight.
 *
 * The end is a same-day clock and the form checks `end > start` in minutes, so
 * `(hours + 1) % 24` turned a 23:00 start into a 00:00 end that reads as
 * ending before it starts: the save was rejected, and the end could not be
 * corrected by hand because this rule reapplies on every change. The last hour
 * of the day therefore ends at the last slot the pickers actually offer —
 * 23:59 would be just as unsaveable in a different way, since the minute
 * select lists the quarters and would simply render blank. A start already at
 * or past the last slot has no same-day end, so nothing is suggested and
 * whatever the form holds is left alone.
 *
 * Exported because the calendar page seeds the same pair when a click or a
 * keypress opens the composer on a given hour, and one rule for "what end
 * follows this start" is the point.
 */
export const nextEndTime = (time: string): string | null => {
  const [hours, minutes] = time.split(":").map(Number)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null

  if (hours < 23) return `${String(hours + 1).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
  return minutes >= 45 ? null : LAST_SLOT
}

const toMinutes = (time?: string) => {
  if (!time) return null
  const [hours, minutes] = time.split(":").map(Number)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null
  return hours * 60 + minutes
}

const isAfter = (start?: string, end?: string) => {
  const startMinutes = toMinutes(start)
  const endMinutes = toMinutes(end)
  if (startMinutes == null || endMinutes == null) return false
  return endMinutes > startMinutes
}

export function useEventFormLogic({
  open,
  mode,
  selectedDate,
  startTime,
  endTime,
  candidateEmail,
  shouldAutoInjectCandidateEmail,
  shouldAutoAdjustEndTime,
  inviteeEmails,
  getValues,
  setValue,
}: UseEventFormLogicArgs) {
  const [inviteeDraft, setInviteeDraft] = useState("")
  const [autoInjectedCandidateEmail, setAutoInjectedCandidateEmail] = useState("")
  const [hasManualEndTimeOverride, setHasManualEndTimeOverride] = useState(false)

  useEffect(() => {
    if (!open) {
      setHasManualEndTimeOverride(false)
    }
  }, [open])

  useEffect(() => {
    if (!open || !shouldAutoAdjustEndTime) return
    if (!selectedDate || !startTime) return
    if (hasManualEndTimeOverride && isAfter(startTime, endTime)) return

    const suggested = nextEndTime(startTime)
    if (!suggested) return

    if (endTime !== suggested) {
      setValue("end_time", suggested)
    }
  }, [open, shouldAutoAdjustEndTime, selectedDate, startTime, endTime, hasManualEndTimeOverride, setValue])

  useEffect(() => {
    if (!open || mode !== "create") return

    const currentList = getValues("invitee_emails") ?? []

    if (!shouldAutoInjectCandidateEmail) {
      if (!autoInjectedCandidateEmail) return
      const cleaned = currentList.filter((email) => email.toLowerCase() !== autoInjectedCandidateEmail.toLowerCase())
      if (cleaned.join(",") !== currentList.join(",")) {
        setValue("invitee_emails", cleaned)
      }
      setAutoInjectedCandidateEmail("")
      return
    }

    if (!candidateEmail) return

    const withoutPreviousAuto = autoInjectedCandidateEmail
      ? currentList.filter((email) => email.toLowerCase() !== autoInjectedCandidateEmail.toLowerCase())
      : currentList

    const exists = withoutPreviousAuto.some((email) => email.toLowerCase() === candidateEmail.toLowerCase())
    const nextList = exists ? withoutPreviousAuto : [candidateEmail, ...withoutPreviousAuto]

    if (nextList.join(",") !== currentList.join(",")) {
      setValue("invitee_emails", nextList)
    }
    setAutoInjectedCandidateEmail(candidateEmail)
  }, [
    open,
    mode,
    shouldAutoInjectCandidateEmail,
    candidateEmail,
    autoInjectedCandidateEmail,
    getValues,
    setValue,
  ])

  const addInviteeEmail = (rawValue: string) => {
    const value = rawValue.trim().replace(/,$/, "")
    if (!value) return
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return

    const exists = inviteeEmails.some((email) => email.toLowerCase() === value.toLowerCase())
    if (!exists) {
      setValue("invitee_emails", [...inviteeEmails, value])
    }
  }

  const handleInviteeDraftKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "," || event.key === "Enter") {
      event.preventDefault()
      addInviteeEmail(inviteeDraft)
      setInviteeDraft("")
    }
  }

  const handleInviteeDraftBlur = () => {
    if (inviteeDraft.trim()) {
      addInviteeEmail(inviteeDraft)
      setInviteeDraft("")
    }
  }

  const removeInviteeEmail = (emailToRemove: string) => {
    const next = inviteeEmails.filter((email) => email !== emailToRemove)
    setValue("invitee_emails", next)
    if (autoInjectedCandidateEmail.toLowerCase() === emailToRemove.toLowerCase()) {
      setAutoInjectedCandidateEmail("")
    }
  }

  const handleStartTimeChange = (value: string) => {
    setValue("start_time", value)

    if (!shouldAutoAdjustEndTime) return
    const suggested = nextEndTime(value)
    if (!suggested) return
    const currentEndTime = getValues("end_time")
    if (hasManualEndTimeOverride && isAfter(value, currentEndTime)) return
    setValue("end_time", suggested)
    setHasManualEndTimeOverride(false)
  }

  const handleEndTimeChange = (value: string) => {
    setValue("end_time", value)
    setHasManualEndTimeOverride(true)
  }

  return {
    inviteeDraft,
    setInviteeDraft,
    handleInviteeDraftKeyDown,
    handleInviteeDraftBlur,
    removeInviteeEmail,
    handleStartTimeChange,
    handleEndTimeChange,
  }
}
